import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// Word list for the game
const words = [
  "school", "college", "student", "teacher", "learning",
  "science", "success", "career", "mentor", "future",
  "passion", "skills", "growth", "teamwork", "goals",
  "talent", "dream", "leader", "degree", "journal",
  "project", "wisdom", "vision", "course", "create"
];

const WordScramble = () => {
  const [currentWord, setCurrentWord] = useState<string>("");
  const [scrambledWord, setScrambledWord] = useState<string>("");
  const [userGuess, setUserGuess] = useState<string>("");
  const [score, setScore] = useState<number>(0);
  const [timeLeft, setTimeLeft] = useState<number>(60);
  const [gameActive, setGameActive] = useState<boolean>(false);
  const [showHint, setShowHint] = useState<boolean>(false);
  const { toast } = useToast();
  
  const scrambleWord = useCallback((word: string): string => {
    const characters = word.split('');
    
    // Keep scrambling until we get a different arrangement
    let scrambled = word;
    while (scrambled === word) {
      for (let i = characters.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [characters[i], characters[j]] = [characters[j], characters[i]]; // Swap
      }
      scrambled = characters.join('');
    }
    
    return scrambled;
  }, []);
  
  const getNextWord = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * words.length);
    const word = words[randomIndex];
    setCurrentWord(word);
    setScrambledWord(scrambleWord(word));
    setShowHint(false);
  }, [scrambleWord]);
  
  const startGame = () => {
    setScore(0);
    setTimeLeft(60);
    setGameActive(true);
    setUserGuess('');
    getNextWord();
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (userGuess.toLowerCase() === currentWord.toLowerCase()) {
      // Correct answer
      setScore(prev => prev + 1);
      toast({
        title: "Correct!",
        description: "+1 point",
      });
      
      // Give some bonus time
      setTimeLeft(prev => Math.min(prev + 5, 90));
      
      // Move to next word
      setUserGuess('');
      getNextWord();
    } else {
      // Wrong answer
      toast({
        title: "Try again",
        description: "That's not the correct word",
        variant: "destructive"
      });
    }
  };
  
  const showWordHint = () => {
    setShowHint(true);
    // Reduce time as penalty for showing hint
    setTimeLeft(prev => Math.max(prev - 5, 1));
    toast({
      title: "Hint Used",
      description: "-5 seconds penalty",
    });
  };
  
  const skipWord = () => {
    toast({
      title: "Word Skipped",
      description: `The word was "${currentWord}"`,
    });
    getNextWord();
    setUserGuess('');
    // Small time penalty for skipping
    setTimeLeft(prev => Math.max(prev - 3, 1));
  };
  
  // Timer
  useEffect(() => {
    let timer: number | undefined;
    
    if (gameActive && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000) as unknown as number;
    } else if (timeLeft === 0 && gameActive) {
      setGameActive(false);
      toast({
        title: "Time's Up!",
        description: `Your final score is ${score}`,
      });
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [gameActive, timeLeft, score, toast]);
  
  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <Badge variant={timeLeft < 15 ? "destructive" : "outline"} className="flex items-center gap-1">
          <Clock className="h-3 w-3" /> {timeLeft}s
        </Badge>
        <Badge variant="outline">Score: {score}</Badge>
      </div>
      
      {!gameActive ? (
        <div className="text-center py-6">
          <h3 className="font-medium mb-2">Word Scramble Challenge</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Unscramble the letters to form a correct word. Each correct answer adds time!
          </p>
          <Button onClick={startGame}>Start Game</Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-muted/50 p-6 rounded-lg text-center">
            <div className="text-2xl font-bold tracking-wide">
              {scrambledWord.split('').join(' ')}
            </div>
            
            {showHint && (
              <div className="mt-2 text-sm">
                Hint: First letter is <span className="font-bold">{currentWord[0]}</span>
              </div>
            )}
          </div>
          
          <form onSubmit={handleSubmit} className="flex items-center gap-2">
            <Input
              type="text"
              value={userGuess}
              onChange={(e) => setUserGuess(e.target.value)}
              placeholder="Your answer..."
              className="flex-1"
              autoFocus
            />
            <Button type="submit">Submit</Button>
          </form>
          
          <div className="flex justify-between">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={showWordHint}
              disabled={showHint}
            >
              Get Hint (-5s)
            </Button>
            
            <Button 
              size="sm" 
              variant="outline" 
              onClick={skipWord}
            >
              Skip Word (-3s)
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WordScramble;
